/**
 * Settings → Benefits → Enrollment Periods (admin).
 *
 * Admin creates the windows during which employees can pick plans.
 * Three flavors:
 *   * Open       — org-wide annual enrollment.
 *   * New hire   — auto-created by Pilot when an enrolled-eligible
 *                  employee is hired. Editable here for backfill.
 *   * Special    — auto-created by an approved life event. Read-only
 *                  on this page (review on the Life Events page).
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  CalendarDays,
  Loader2,
  Plus,
  Save,
} from "lucide-react";

import { pageTransition } from "@/lib/utils";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/hooks/useToast";
import {
  createPeriod,
  listPeriods,
  listPlans,
  type BenefitPlan,
  type EnrollmentPeriod,
} from "@/api/benefits";

const STATUS_TONE: Record<string, "info" | "success" | "default"> = {
  upcoming: "info",
  active: "success",
  closed: "default",
};

export function EnrollmentPeriodsPage() {
  const [periods, setPeriods] = useState<EnrollmentPeriod[] | null>(null);
  const [plans, setPlans] = useState<BenefitPlan[]>([]);
  const [open, setOpen] = useState(false);

  const reload = async () => {
    try {
      const [p, pl] = await Promise.all([listPeriods(true), listPlans()]);
      setPeriods(p);
      setPlans(pl);
    } catch {
      setPeriods([]);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Enrollment Periods"
        subtitle="Define open enrollment windows. Special enrollment periods are auto-created by approved life events."
      />

      <Card>
        <CardHeader>
          <CardTitle>Periods</CardTitle>
          <Button size="sm" onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" />
            New period
          </Button>
        </CardHeader>

        {!periods ? (
          <div className="py-10 text-center text-text-muted">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        ) : periods.length === 0 ? (
          <div className="py-10 text-center text-sm text-text-muted">
            <CalendarDays className="h-8 w-8 mx-auto mb-2 opacity-40" />
            No enrollment periods yet. Create one to open benefits to your team.
          </div>
        ) : (
          <ul className="divide-y divide-border/40">
            {periods.map((p) => (
              <li
                key={p.id}
                className="py-3 flex items-start justify-between gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-text-primary truncate">
                      {p.name}
                    </span>
                    <Badge variant={STATUS_TONE[p.status] || "default"}>
                      {p.status}
                    </Badge>
                    <Badge variant="default">
                      {p.period_type.replace(/_/g, " ")}
                    </Badge>
                    <span className="text-xs text-text-muted">
                      {p.start_date} → {p.end_date}
                    </span>
                  </div>
                  <p className="text-xs text-text-muted mt-0.5">
                    {p.eligible_plan_ids.length} eligible plan
                    {p.eligible_plan_ids.length === 1 ? "" : "s"}
                    {p.auto_notify ? " · auto-notify on" : ""}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <PeriodModal
        isOpen={open}
        onClose={() => setOpen(false)}
        plans={plans}
        onSaved={async () => {
          setOpen(false);
          await reload();
        }}
      />
    </motion.div>
  );
}

interface PeriodModalProps {
  isOpen: boolean;
  onClose: () => void;
  plans: BenefitPlan[];
  onSaved: () => void;
}

function PeriodModal({ isOpen, onClose, plans, onSaved }: PeriodModalProps) {
  const { addToast } = useToast();
  const [name, setName] = useState("");
  const [type, setType] = useState<"open" | "new_hire" | "special">("open");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [eligible, setEligible] = useState<string[]>([]);
  const [autoNotify, setAutoNotify] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName("");
    setType("open");
    setStart("");
    setEnd("");
    setEligible(plans.filter((p) => p.is_active).map((p) => p.id));
    setAutoNotify(true);
  }, [isOpen, plans]);

  const togglePlan = (id: string) => {
    setEligible((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
    );
  };

  const save = async () => {
    if (!name.trim() || !start || !end) {
      addToast("error", "Name, start, and end are required");
      return;
    }
    setSaving(true);
    try {
      await createPeriod({
        name: name.trim(),
        period_type: type,
        start_date: start,
        end_date: end,
        eligible_plan_ids: eligible,
        auto_notify: autoNotify,
      });
      addToast("success", "Period created");
      onSaved();
    } catch {
      addToast("error", "Couldn't create period");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="New enrollment period"
      size="lg"
    >
      <div className="space-y-3">
        <Input
          label="Period name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="2026 Open Enrollment"
        />
        <Select
          label="Type"
          value={type}
          onChange={(e) => setType(e.target.value as "open" | "new_hire" | "special")}
          options={[
            { value: "open", label: "Open Enrollment" },
            { value: "new_hire", label: "New Hire" },
            { value: "special", label: "Special Enrollment" },
          ]}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Start date"
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
          <Input
            label="End date"
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
          />
        </div>

        <div className="rounded-md border border-border/40 bg-background/40 p-3">
          <p className="text-xs font-medium text-text-muted mb-2">
            Eligible plans
          </p>
          {plans.length === 0 ? (
            <p className="text-xs text-text-muted">
              Add benefit plans first.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {plans.map((p) => (
                <li key={p.id}>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={eligible.includes(p.id)}
                      onChange={() => togglePlan(p.id)}
                      className="rounded border-border text-accent focus:ring-accent"
                    />
                    <span className="text-text-primary">{p.name}</span>
                    <span className="text-xs text-text-muted">
                      {p.carrier_name} · {p.plan_type.replace(/_/g, " ")}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={autoNotify}
            onChange={(e) => setAutoNotify(e.target.checked)}
            className="rounded border-border text-accent focus:ring-accent"
          />
          Auto-notify employees when this period opens
        </label>

        <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} isLoading={saving}>
            <Save className="h-4 w-4" />
            Create period
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default EnrollmentPeriodsPage;
